import React from "react"

import { LANGUAGE_SECTIONS, LanguageSection } from "./language_sections"

const CARD_CLASS =
  "flex items-start gap-2 rounded-lg border border-secondary-200 p-3 text-left hover:border-primary-300 hover:bg-secondary-50"

export interface SectionCardProps {
  number: number
  icon: React.ReactNode
  label: string
  description: string
  /** Real link target (permalinked page) – renders an `<a>`. */
  href?: string
  /** Nav callback (modal) – renders a `<button>`. Ignored when `href` is set. */
  onClick?: () => void
  className?: string
}

/**
 * One "chapter" card. Polymorphic on purpose: pass `href` for a real link (the cheat-sheet pages) or
 * `onClick` for a modal nav button – same visual either way, so the section list can be shared across
 * both surfaces.
 */
export const SectionCard: React.FC<SectionCardProps> = ({
  number,
  icon,
  label,
  description,
  href,
  onClick,
  className,
}) => {
  const inner = (
    <>
      <span className="mt-0.5 shrink-0 text-muted-foreground">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Chapter {number}
        </span>
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-xs text-muted-foreground">
          {description}
        </span>
      </span>
    </>
  )
  const cls = className ?? CARD_CLASS

  return href !== undefined ? (
    <a href={href} className={cls}>
      {inner}
    </a>
  ) : (
    <button type="button" onClick={onClick} className={cls}>
      {inner}
    </button>
  )
}

export interface SectionCardsProps {
  /**
   * Per-section renderer, so the same LANGUAGE_SECTIONS list renders as page links or modal nav
   * buttons without duplicating the dataset. Receives the section and its 1-based chapter number.
   */
  renderCard: (section: LanguageSection, number: number) => React.ReactNode
  className?: string
  testId?: string
}

/**
 * The shared "table of contents" over LANGUAGE_SECTIONS – one dataset, rendered as cards. The caller
 * supplies `renderCard` (e.g. a linked `SectionCard` on the pages, an `onClick` one in the modal), so
 * the modal card view and the cheat-sheet card view stay in sync.
 */
export const SectionCards: React.FC<SectionCardsProps> = ({
  renderCard,
  className,
  testId,
}) => (
  <div
    className={className ?? "grid grid-cols-1 gap-2 sm:grid-cols-2"}
    data-testid={testId}
  >
    {LANGUAGE_SECTIONS.map((section, index) => (
      <React.Fragment key={section.key}>
        {renderCard(section, index + 1)}
      </React.Fragment>
    ))}
  </div>
)
